import shareScopeConsts from 'src/consts/share_scope';
import { COMMENT_SOURCE_TYPE_SELF } from 'src/consts/comment_source_type';
import {
  getNearKps,
  fetchFileAsObjectInfo,
  MAX_SAVABLE_FILES_COUNT,
  ATTACHMENT_FILE_MAX_MB_DEFAULT,
  ACCEPTED_FILE_TYPES,
} from 'src/lib/commentHelper';
import {
  computed,
  onBeforeUnmount,
  reactive,
  watch,
  type ComputedRef,
  type Reactive,
} from 'vue';
import type { CandidateFile, QueryArea } from 'src/models';
import type { GIComment, GIKp } from 'src/models/geoItem';
import type {
  Comment,
  EditComment,
  LatLonOnly,
  LatLonWithParent,
  CommentEditProps,
  CommentEditEmit,
} from 'src/models/comment';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import type { MastersContext } from 'src/models/master';
import { splitByLineBreak } from 'src/lib/utils';
import { dtFormat } from 'src/lib/dateHelper';
import {
  convertFilesToFileObjs,
  fileTypeIconFromFilePath,
  isImageFileType,
} from 'src/lib/fileHelper';

export interface MapElemInfoCommentState {
  mode: 'show' | 'edit';
  errorMsgs: string[];
  comment: Comment;
  // 動画画面でプレイヤーから切り出して渡される画像またはファイル選択の画像を保持する
  candidateFiles: CandidateFile[];
  updateTs: boolean;
  selectedKpObj: GIKp | null;
  editItem: EditComment;
}

const DEFAULT_SHARE_SCOPE = shareScopeConsts.SHARE_SCOPE_GROUP3;

const initComment = (): Comment => ({
  id: 0,
  angle: null,
  commentType: '',
  content: '',
  direction: null,
  groupInfo: {
    g1id: -1,
    g2id: -1,
    g2name: '',
    g3id: -1,
    g3name: '',
  },
  image1Path: null,
  damageRank: null,
  kpCalc: null,
  kpUid: null,
  location: { lat: '', lon: '' },
  movieId: null,
  parentId: null,
  roadNameDisp: null,
  shareScope: DEFAULT_SHARE_SCOPE,
  sourceType: COMMENT_SOURCE_TYPE_SELF,
  ts: new Date(), // 型に合わせるために何か入れるが、使用されない.
  userDisplayName: '',
  userId: -1,
  userRole: -1,
  commentFiles: [],
});

const initEditItem = (): EditComment => ({
  ...initComment(),
  updateTs: false,
  saveFileObjs: [],
  saveFiles: [],
  removeFileIds: [],
});

const isPropsElemDataGIComment = (arg: LatLonOnly | LatLonWithParent | GIComment): arg is GIComment => {
  return 'id' in arg;
};
const isPropsElemDataLatLonWithComment = (arg: LatLonOnly | LatLonWithParent | GIComment): arg is GIComment => {
  if (isPropsElemDataGIComment(arg)) return false;

  return 'parentId' in arg;
};

const toInitialCommentObj = (arg: LatLonOnly | LatLonWithParent): Comment => {
  const lat = arg.lat.toString();
  const lon = arg.lon.toString();
  let kpUid = null;
  let parentId = null;
  if (isPropsElemDataLatLonWithComment(arg)) {
    kpUid = arg.kpUid;
    parentId = arg.parentId;
  }
  // FIXME 数箇所で、id==0 であることを利用してisNewかどうか判定している箇所があるが、変えたい.
  return {
    ...initComment(),
    kpUid,
    location: { lat, lon },
    parentId,
  };
};

type CommentEditContext = {
  state: MapElemInfoCommentState;
  nearKps: ComputedRef<GIKp[]>;
  commentKpObject: ComputedRef<GIKp | null | undefined>;
  sortedPreviewFiles: ComputedRef<CandidateFile[]>;
  startEdit: () => void;
  copyLink: () => void;
  showPrevious: () => void;
  showNext: () => void;
  confirmDelete: () => void;
  doAreaMovieSearch: () => void;
  onSelectedKpObjChanged: () => void;
  handleFileUpload: (fileList: FileList) => Promise<void>;
  initDisplay: () => Promise<void>;
  removeFile: (fileId: number) => void;
  save: () => Promise<void>;
  showImage: (image: CandidateFile) => void;
  cancelEdit: () => Promise<void>;
  selectImage: (image: CandidateFile) => void;
  nearKpThresholdMeters: number;
  fileTypeIconFromFilePath: (filePath: string) => string;
  isImageFileType: (filePath: string) => boolean;
  ACCEPTED_FILE_TYPES: typeof ACCEPTED_FILE_TYPES;
};

export function useCommentEdit(
  props: Required<CommentEditProps>,
  emit: CommentEditEmit,
  masters: Reactive<MastersContext>,
): CommentEditContext {
  const state = reactive<MapElemInfoCommentState>({
    mode: 'show',
    errorMsgs: [],
    comment: initComment(),
    candidateFiles: [],
    updateTs: false,
    selectedKpObj: null,
    editItem: initEditItem(),
  });

  const startEdit = () => {
    state.mode = 'edit';
    state.updateTs = false;
    state.selectedKpObj = commentKpObject.value ?? (nearKps.value.length > 0 ? nearKps.value[0] : null);
    const editItem: EditComment = {
      ...state.comment,
      commentType: state.comment.commentType || 'share',
      shareScope: state.comment.shareScope || DEFAULT_SHARE_SCOPE,
      content: splitByLineBreak(state.comment.content).join('\n'),
      angle: null,
      updateTs: false,
      saveFileObjs: [],
      saveFiles: [],
      removeFileIds: [],
    };
    if (state.selectedKpObj && editItem.angle == null) {
      editItem.angle = state.selectedKpObj.angle;
    }
    // 動画プレイヤーで付箋追加の場合は候補画像を表示する
    if (props.elem.candidateImages) {
      props.elem.candidateImages.forEach((image, idx) => {
        if (!image) return;

        const tmpImageName = 'cut_' + idx + '.jpg';
        state.candidateFiles.push({
          id: idx,
          content: convertBlobToImageSrc(image),
          type: image.type,
          name: tmpImageName,
          path: tmpImageName,
          selected: false,
        });
      });
      // 1枚だったら選択しておく
      if (state.candidateFiles.length === 1) {
        state.candidateFiles[0].selected = true;
      }
    }
    state.editItem = editItem;
    emit('component-resized');
  };
  const initDisplay = async() => {
    releaseAllFiles();
    state.errorMsgs = [];
    const maybeComment = props.elem.data;
    state.comment = isPropsElemDataGIComment(maybeComment)
      ? {
        ...maybeComment,
        location: { lat: maybeComment.location.lat.toString(), lon: maybeComment.location.lon.toString() },
      }
      : toInitialCommentObj(maybeComment);
    if (props.isCreateSingleNewElement) {
      startEdit();
    } else {
      state.mode = 'show';
    }
    if (!state.comment.commentFiles || state.comment.commentFiles.length === 0) return;

    await prepareCandidateFiles();
  };
  const prepareCandidateFiles = async(): Promise<void> => {
    const commentFilePromises = state.comment.commentFiles.map<Promise<CandidateFile | null>>(async(file) => {
      const savedFileInfo = await fetchFileAsObjectInfo(state.comment.id, file.id);
      if (!savedFileInfo || !savedFileInfo.url || !savedFileInfo.type) return null;

      return {
        id: file.id,
        content: savedFileInfo.url,
        type: savedFileInfo.type,
        name: file.fileName ?? '',
        path: file.filePath,
        selected: false,
      };
    });
    state.candidateFiles = (await Promise.all(commentFilePromises))
      .filter((e): e is CandidateFile => e !== null);
    emit('component-resized');
  };

  const releaseAllFiles = () => {
    if (state.editItem.saveFileObjs) {
      state.editItem.saveFileObjs.forEach(elm => URL.revokeObjectURL(elm.content));
    }
    state.editItem.saveFileObjs = [];
    state.editItem.removeFileIds = [];
    state.candidateFiles.forEach(elm => URL.revokeObjectURL(elm.content));
    state.candidateFiles = [];
  };
  onBeforeUnmount(() => {
    releaseAllFiles();
  });
  const convertBlobToImageSrc = (blob: Blob) => {
    return URL.createObjectURL(blob);
  };
  watch(() => props.elem, initDisplay);
  const createFailedMsg = '付箋追加に失敗しました。';
  const updateFailedMsg = '付箋更新に失敗しました。';
  watch(() => props.updateFailed, () => {
    if (!state.errorMsgs.includes(updateFailedMsg)) {
      state.errorMsgs.push(updateFailedMsg);
    }
  });
  watch(() => props.createFailed, () => {
    if (!state.errorMsgs.includes(createFailedMsg)) {
      state.errorMsgs.push(createFailedMsg);
    }
  });

  const nearKpThresholdMeters = 100;
  const nearKps = computed(() => {
    const elemData = props.elem.data;
    const elemLocation = isPropsElemDataGIComment(elemData) ? elemData.location : elemData;
    return getNearKps(
      { lat: elemLocation.lat.toString(), lon: elemLocation.lon.toString() },
      masters.kp.kpMap,
      masters.kp.roadNameDispMap,
      nearKpThresholdMeters,
    );
  });
  const commentKpObject = computed(() => {
    const maybeComment = props.elem.data;
    if (!isPropsElemDataGIComment(maybeComment)) return null;

    return nearKps.value.find(e => e.kpUid === maybeComment.kpUid);
  });
  const sortedPreviewFiles = computed(() => {
    return [...state.candidateFiles].sort((a, b) => a.id - b.id);
  });
  const copyLink = () => {
    const maybeComment = props.elem.data;
    if (!isPropsElemDataGIComment(maybeComment)) return;

    const link = `${location.origin}/?comment_id=${maybeComment.id}`;
    window.navigator.clipboard.writeText(link);
  };
  const showPrevious = () => {
    emit('show-previous', props.elem);
  };
  const showNext = () => {
    emit('show-next', props.elem);
  };
  const confirmDelete = () => {
    emit('confirm-delete', {
      dataName: 'comment',
      data: state.comment,
      confirmMessage: '選択中の付箋を削除してもよろしいですか？',
    });
  };
  const doAreaMovieSearch = () => {
    const maybeComment = props.elem.data;
    const commentLocation = isPropsElemDataGIComment(maybeComment) ? maybeComment.location : maybeComment;
    const queryArea: QueryArea = {
      lat: commentLocation.lat,
      lon: commentLocation.lon,
      radius: 500,
    };
    const kpToCompare = commentKpObject.value;
    if (isPropsElemDataGIComment(maybeComment) && kpToCompare) {
      queryArea.resultSortFunc = (a, b) => {
        const aHasSimilarAttributes =
          a.startKp.roadNameDisp === kpToCompare.roadNameDisp &&
          a.startKp.direction === kpToCompare.direction;
        const bHasSimilarAttributes =
          b.startKp.roadNameDisp === kpToCompare.roadNameDisp &&
          b.startKp.direction === kpToCompare.direction;
        if (aHasSimilarAttributes && !bHasSimilarAttributes) return -1;
        if (!aHasSimilarAttributes && bHasSimilarAttributes) return 1;

        return 0;
      };
    }
    emit('area-movie-search', queryArea);
  };
  const onSelectedKpObjChanged = () => {
    state.editItem = {
      ...state.editItem,
      angle: state.selectedKpObj && state.selectedKpObj.angle !== null ? state.selectedKpObj.angle : null,
    };
  };
  const handleFileUpload = async(fileList: FileList) => {
    state.errorMsgs = [];
    const uploadFilesCount = fileList.length + state.candidateFiles.length;
    try {
      if (uploadFilesCount > MAX_SAVABLE_FILES_COUNT) {
        throw Error('添付ファイルの最大数は' + MAX_SAVABLE_FILES_COUNT + '個です。');
      }
      // 新規ファイルのidを連番で割り振る(実際のidはサーバー側で振られる)
      const maxId = state.candidateFiles.map(e => e.id).reduce((a, b) => Math.max(a, b), 0);
      const fileObjs = await convertFilesToFileObjs(fileList, maxId, ATTACHMENT_FILE_MAX_MB_DEFAULT);
      state.candidateFiles.push(...fileObjs);
      state.editItem.saveFileObjs.push(...fileObjs);
    } catch (error) {
      state.errorMsgs.push(...getErrorMessages(error as AxiosError));
    }
  };
  const removeFile = (fileId: number) => {
    state.candidateFiles = state.candidateFiles.filter(e => e.id !== fileId);
    const saveFile = state.editItem.saveFileObjs.find(e => e.id === fileId);
    if (saveFile) {
      state.editItem.saveFileObjs = state.editItem.saveFileObjs.filter(e => e.id !== fileId);
      return;
    }
    state.editItem.removeFileIds.push(fileId);
  };
  const save = async() => {
    // 更新の場合のみ通知フラグを送信する。
    if (!props.isCreateSingleNewElement) {
      state.editItem.updateTs = state.updateTs;
    }
    // 動画プレイヤーで「現在位置に付箋を追加」をした場合は選択した画像を保存する
    const selectedFile = state.candidateFiles.find(e => e.selected);
    if (selectedFile && props.elem.candidateImages) {
      const selectedImage = props.elem.candidateImages[selectedFile.id];
      const selectedImageFile = new File([selectedImage], 'selectedImage', { type: selectedImage.type });
      state.editItem.saveFiles.push(selectedImageFile);
    } else {
      state.editItem.saveFiles = state.editItem.saveFileObjs.map(e => e.file);
    }
    state.editItem.kpUid = state.selectedKpObj ? state.selectedKpObj.kpUid : null;
    emit('update', { dataName: 'comment', data: state.editItem });
  };
  const showImage = (image: CandidateFile) => {
    const modalTitle = state.comment.content || '';
    const commentTs = dtFormat(state.comment.ts, 'yyyymmddHHMMSS');
    const downloadFilenameTemplate = `付箋添付画像_${state.comment.id}_${commentTs}_$TIMESTAMP.jpg`;
    const imageFiles = sortedPreviewFiles.value.filter(f => isImageFileType(f.path));
    const initialIndex = imageFiles.findIndex(f => f.id === image.id);
    const imageUrls = imageFiles.map(f => f.content);
    emit('show-image', {
      modalTitle,
      imageUrls,
      downloadFilenameTemplate,
      initialIndex: Math.max(initialIndex, 0),
    });
  };

  const cancelEdit = async() => {
    releaseAllFiles();
    if (props.forceShowCancelButton) {
      emit('cancel-edit');
      return;
    }
    state.mode = 'show';
    // 操作したファイルを元に戻す
    await prepareCandidateFiles();
    emit('component-resized');
  };
  const selectImage = (image: CandidateFile) => {
    state.candidateFiles.forEach(candidate => {
      if (candidate !== image) {
        candidate.selected = false;
      }
    });
    image.selected = !image.selected;
  };

  return {
    state,
    nearKps,
    commentKpObject,
    sortedPreviewFiles,
    startEdit,
    copyLink,
    showPrevious,
    showNext,
    confirmDelete,
    doAreaMovieSearch,
    onSelectedKpObjChanged,
    handleFileUpload,
    initDisplay,
    removeFile,
    save,
    showImage,
    cancelEdit,
    selectImage,
    nearKpThresholdMeters,
    fileTypeIconFromFilePath,
    isImageFileType,
    ACCEPTED_FILE_TYPES,
  };
}
